import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { StatBox } from '../../components/ui/StatBox.jsx';
import { FRAIS_CAT } from '../../constants/accounting';
import { useMaFiche } from '../../api/maFiche';
import { useReglages } from '../../api/reglages.jsx';
import { useAchatsJoints } from '../../components/ListeDuClient.jsx';
import { usePaiements } from '../../components/PaiementsRecus.jsx';
import {
  CLE_REGLE_ACOMPTES,
  piecesComptables,
  reglesAcomptes,
} from '../../lib/acomptes-anterieurs';
import { tauxCompta, tauxPatronauxTotal } from '../../lib/charges';
import { useChargesAnnexes } from '../../lib/charges-annexes';
import { DEVISE, fmt } from '../../lib/currency';
import { depensesHt } from '../../lib/depenses-ht';
import { chargesReparties } from '../../lib/depenses-reparties';
import { modulesSouscrits } from '../../lib/modules';
import { litDate } from '../../lib/periodes';
import { totalAEncaisser } from '../../lib/impayes';
import { compteDansLeCa, htDocument, htVente, signeCa } from '../../lib/vat';
import { estIndependant } from '../equipe/EmpEditor.jsx';
import { C } from '../../theme/palette';

export function StatsPage({
  onNav,
  soc,
  factures,
  devis,
  ventes,
  clients,
  frais,
  chantiers,
  employes,
  estMixte,
  univers,
  setUnivers,
}) {
  // L'année en cours, comptée comme l'accueil et la Compta : factures émises
  // (ni brouillons ni annulées, avoirs en moins), datées de l'année choisie
  // pour un acompte, et toutes les dépenses — achats des chantiers, salaires,
  // véhicules, comptable. La page additionnait toutes les années, les
  // brouillons et les factures annulées, et retranchait les seuls frais : son
  // bénéfice annonçait un gain là où la Compta voyait une perte.
  const maintenant = new Date();
  const annee = maintenant.getFullYear();
  const deLAnnee = (d) => litDate(d, maintenant)?.getFullYear() === annee;
  const { reglageJson } = useReglages();
  const { paiements } = usePaiements((factures || []).length);
  const facs = piecesComptables(
    factures,
    paiements,
    reglesAcomptes(reglageJson(CLE_REGLE_ACOMPTES, null)),
  ).filter((f) => compteDansLeCa(f) && deLAnnee(f.dateCompta || f.date));
  const htF = (f) => signeCa(f) * htDocument(f);
  // Les ventes au comptoir sont du chiffre d'affaires comme les factures :
  // le magasin voyait son chiffre et son bénéfice amputés de toute la caisse.
  const caVentes = (ventes || [])
    .filter((v) => deLAnnee(v.date))
    .reduce((a, v) => a + htVente(v), 0);
  const caTotal = facs.reduce((a, f) => a + htF(f), 0) + caVentes;
  // Ce qui reste dû, toutes années confondues, acomptes reçus déduits.
  const impaye = totalAEncaisser(factures, devis);
  const maFiche = useMaFiche();
  const achatsJoints = useAchatsJoints((factures || []).length);
  const { vehicules, comptableAnnuel } = useChargesAnnexes();
  const depTotal = depensesHt({
    frais,
    chantiers,
    achatsJoints,
    factures,
    devis,
    employes,
    soc,
    chargesSociales:
      tauxPatronauxTotal(
        tauxCompta(soc, annee),
        modulesSouscrits(estMixte ? 'mixte' : soc?.secteur).batiment,
      ) / 100,
    exclu: (e) => e?.patronIndependant || (estIndependant(soc?.forme) && maFiche.estLaMienne(e)),
    maintenant,
    vehicules,
    comptableAnnuel,
  })
    .filter((d) => deLAnnee(d.date))
    .reduce((a, d) => a + d.montant, 0);
  const benef = caTotal - depTotal;
  // Une dépense répartie sur les mois qu'elle couvre compte pour ses mois de
  // l'année, comme dans le bénéfice ci-dessus.
  const fraisAnnee = chargesReparties(frais, soc).filter((x) => deLAnnee(x.dateCompta || x.date));
  const mois = [
    'Jan',
    'Fév',
    'Mar',
    'Avr',
    'Mai',
    'Juin',
    'Juil',
    'Août',
    'Sep',
    'Oct',
    'Nov',
    'Déc',
  ];
  const parMois = Array(12).fill(0);
  facs.forEach((f) => {
    const m = (f.dateCompta || f.date || '').match(/\.(\d{2})\./);
    if (m) {
      const mi = +m[1] - 1;
      if (mi >= 0 && mi < 12) parMois[mi] += htF(f);
    }
  });
  const maxMois = Math.max(1, ...parMois);
  const parClient = {};
  facs.forEach((f) => {
    const c = (clients || []).find((x) => x.id === f.clientId);
    const nom = c ? c.nom : '—';
    parClient[nom] = (parClient[nom] || 0) + htF(f);
  });
  const topCli = Object.entries(parClient)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const maxCli = Math.max(1, ...topCli.map((x) => x[1]), 1);
  const parCat = {};
  fraisAnnee.forEach((x) => {
    const c = (FRAIS_CAT[x.type] || {}).lib || x.type || 'Autre';
    parCat[c] = (parCat[c] || 0) + (+x.montant || 0);
  });
  const topCat = Object.entries(parCat)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const maxCat = Math.max(1, ...topCat.map((x) => x[1]), 1);
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      pageTitle="📊 Statistiques"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}
      >
        <StatBox label="Chiffre d'affaires" value={DEVISE + ' ' + fmt(caTotal)} color={C.green} />
        <StatBox label="Bénéfice" value={DEVISE + ' ' + fmt(benef)} color={C.navy} />
        <StatBox label="Impayé" value={DEVISE + ' ' + fmt(impaye)} color={C.red} />
      </div>
      <Card style={{ marginBottom: 12 }}>
        <Bar icon="📈" title="Chiffre d'affaires par mois" color={C.teal} sub="Facturé (HT)" />
        <div
          style={{
            padding: '14px 13px 10px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 5,
            height: 150,
          }}
        >
          {parMois.map((v, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                height: '100%',
              }}
            >
              <div
                style={{
                  marginTop: 'auto',
                  width: '100%',
                  // `C.tealD` n'existe pas : le dégradé était invalide et
                  // les barres, sans fond, restaient invisibles.
                  background: 'linear-gradient(180deg,' + C.teal + ',' + C.navyM + ')',
                  borderRadius: '4px 4px 0 0',
                  height: (v / maxMois) * 100 + '%',
                  minHeight: v > 0 ? 3 : 0,
                }}
              />
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{mois[i]}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card style={{ marginBottom: 12 }}>
        <Bar icon="👥" title="Top clients" color={C.navy} sub="Par chiffre d'affaires" />
        <div style={{ padding: 13 }}>
          {topCli.length === 0 ? (
            <div style={{ textAlign: 'center', color: C.muted, fontSize: 11, padding: 14 }}>
              Aucune donnée pour le moment.
            </div>
          ) : (
            topCli.map(([nom, v], i) => (
              <div key={i} style={{ marginBottom: 11 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{nom}</span>
                  <b>{DEVISE + ' ' + fmt(v)}</b>
                </div>
                <div
                  style={{ height: 9, background: C.navyXL, borderRadius: 99, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: (v / maxCli) * 100 + '%',
                      background: C.teal,
                      borderRadius: 99,
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
      <Card style={{ marginBottom: 12 }}>
        <Bar icon="🧾" title="Dépenses par catégorie" color={C.red} sub="Toutes tes dépenses" />
        <div style={{ padding: 13 }}>
          {topCat.length === 0 ? (
            <div style={{ textAlign: 'center', color: C.muted, fontSize: 11, padding: 14 }}>
              Aucune dépense saisie.
            </div>
          ) : (
            topCat.map(([nom, v], i) => (
              <div key={i} style={{ marginBottom: 11 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{nom}</span>
                  <b>{DEVISE + ' ' + fmt(v)}</b>
                </div>
                <div
                  style={{ height: 9, background: C.navyXL, borderRadius: 99, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: (v / maxCat) * 100 + '%',
                      background: C.red,
                      borderRadius: 99,
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
      <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', margin: '2px 0 6px' }}>
        Calculé automatiquement depuis tes factures et dépenses.
      </div>
    </Shell>
  );
}
